import React, { useEffect, useMemo } from "react";
import { Drawer, Button } from 'components/common';
import { useForm, Controller } from "react-hook-form";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import DatePicker from "react-datepicker";
import Select from "react-select";
import { resetNestedParams, setFilterTag, getForwarders } from "redux/crm/logistic/logisticTrackingSlice";
import { format } from 'date-fns';
import { MdOutlineRestartAlt, MdSearch } from "react-icons/md";
import { FaFilter } from "react-icons/fa";
import { useDateRange } from "helpers/useDateRange";
import { SHIPMENT_DELIVERY_TERM } from "app_config";
import { useContainerTypeOptions } from "./useContainerTypes";
import PropTypes from "prop-types";
import { getCountries } from "redux/settingsSlice";
import AsyncSelect from 'react-select/async';
import { INPUT_CLS } from 'helpers/formStyles';

const FieldRow = ({ label, children }) => (
  <div className="tw-grid tw-grid-cols-3 tw-gap-3 tw-mb-3 tw-items-center">
    <label className="tw-text-right tw-text-sm tw-text-slate-600 tw-font-medium">{label}</label>
    <div className="tw-col-span-2">{children}</div>
  </div>
);
FieldRow.propTypes = { label: PropTypes.string.isRequired, children: PropTypes.node };

const SectionTitle = ({ children }) => (
  <div className="tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wider tw-text-slate-400 tw-mb-3 tw-mt-5 tw-pb-1 tw-border-b tw-border-slate-100">
    {children}
  </div>
);
SectionTitle.propTypes = { children: PropTypes.node };

const LogisticTrackingFilterAdvanceModal = ({ showModal, setshowModal, data, entryKey, onApply, onReset }) => {
  const { control, register, handleSubmit, reset } = useForm();
  const { countryData } = useSelector(state => state.settings);
  const dispatch = useDispatch();

  const { pageSize } = data || {};
  const key = entryKey;

  const { startDate: etaFromDate, endDate: etaEndDate, onChangeDateRange: onChangeETADate } = useDateRange();
  const { startDate: customsFromDate, endDate: customsEndDate, onChangeDateRange: onChangeCustomsDate } = useDateRange();
  const { startDate: submitedFromDate, endDate: submitedEndDate, onChangeDateRange: onChangeSubmitedDate } = useDateRange();

  useEffect(() => {
    if (!countryData?.length) dispatch(getCountries());
  }, [countryData?.length, dispatch]);

  const loadForwarderOptions = async (inputValue) => {
    try {
      const params = { page: 1, per_page: 30 };
      if (inputValue && inputValue.length > 2) params.name_cont = inputValue;
      const response = await dispatch(getForwarders(params)).unwrap();
      return response.data.map(item => ({ value: item.id, label: item.name }));
    } catch {
      return [];
    }
  };

  const onSubmit = (formData) => {
    const isoDate = value => value ? format(value, 'yyyy-MM-dd') : null;
    const forwarderIds = formData.forwarder_id ? formData.forwarder_id.map(o => o.value) : [];
    const forwarderNames = formData.forwarder_id ? formData.forwarder_id.map(o => o.label) : [];

    const newData = {
      project_j_number_cont: formData.job_number,
      project_q_number_cont: formData.quote_number,
      plan_names_cont: formData.phase_name,
      project_company_name_cont: formData.buyer,
      bill_no_cont: formData.bill_no,
      shipment_number_cont: formData.shipment_no,
      customs_declaration_number_cont: formData.customs_no,
      project_destination_port_cont: formData.destination_port,
      estimate_time_arrival_gteq: isoDate(etaFromDate),
      estimate_time_arrival_lteq: etaEndDate ? `${isoDate(etaEndDate)}T23:59:59` : null,
      customs_declaration_date_gteq: isoDate(customsFromDate),
      customs_declaration_date_lteq: isoDate(customsEndDate),
      project_delivery_term_in: formData.delivery_term,
      project_pj_country_in: formData.location,
      forwarder_id_in: forwarderIds,
      container_type_in: formData.container_type,
      created_by_cont: formData.created_by,
      created_at_gteq: isoDate(submitedFromDate),
      created_at_lteq: submitedEndDate ? `${isoDate(submitedEndDate)}T23:59:59` : null,
      page: 1,
      per_page: pageSize,
    };

    const dataFilter = [
      { id: "project_j_number_cont",        name: "Job number",       value: formData.job_number },
      { id: "project_q_number_cont",         name: "Quote number",     value: formData.quote_number },
      { id: "plan_names_cont",               name: "Phase name",       value: formData.phase_name },
      { id: "project_company_name_cont",     name: "Buyer",            value: formData.buyer },
      { id: "project_destination_port_cont", name: "Destination port", value: formData.destination_port },
      { id: "bill_no_cont",                  name: "Bill no.",         value: formData.bill_no },
      { id: "shipment_number_cont",          name: "Shipment no.",     value: formData.shipment_no },
      { id: "customs_declaration_number_cont", name: "Customs no.",    value: formData.customs_no },
      { id: "created_by_cont",               name: "Submitted by",     value: formData.created_by },
      { id: "project_delivery_term_in",      name: "Delivery term",    value: formData.delivery_term },
      { id: "project_pj_country_in",         name: "Location",         value: formData.location },
      { id: "forwarder_id_in",               name: "Forwarder",        value: forwarderNames },
      { id: "container_type_in",             name: "Container type",   value: formData.container_type },
    ];

    if (etaFromDate && etaEndDate)
      dataFilter.push({ id: "estimate_time_arrival_gteq", name: "ETA", value: [isoDate(etaFromDate), isoDate(etaEndDate)] });
    if (customsFromDate && customsEndDate)
      dataFilter.push({ id: "customs_declaration_date_gteq", name: "Customs date", value: [isoDate(customsFromDate), isoDate(customsEndDate)] });
    if (submitedFromDate && submitedEndDate)
      dataFilter.push({ id: "created_at_gteq", name: "Submitted date", value: [isoDate(submitedFromDate), isoDate(submitedEndDate)] });

    const hasValue = value => value !== null && value !== undefined && value !== '' && (!Array.isArray(value) || value.length > 0);
    dispatch(setFilterTag({ key, tags: dataFilter.filter(d => hasValue(d.value)) }));

    const filtered = Object.fromEntries(
      Object.entries(newData).filter(([, v]) => hasValue(v))
    );

    try {
      onApply(filtered);
      setshowModal(false);
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleReset = () => {
    reset();
    onChangeETADate([null, null]);
    onChangeCustomsDate([null, null]);
    onChangeSubmitedDate([null, null]);
    dispatch(resetNestedParams({ key }));
    onReset();
  };

  const deliveryTermOptions = useMemo(() => SHIPMENT_DELIVERY_TERM.map(item => ({ value: item, label: item })), []);
  const containerTypeOptions = useContainerTypeOptions();
  const countryOptions = useMemo(() => countryData?.map(o => ({ value: o.country, label: o.name })) ?? [], [countryData]);

  const inputClass = INPUT_CLS;
  const footer = (
    <>
      <Button type="submit" form="logistic-filter-form" size="sm" variant="primary">
        <MdSearch size={15} /> Search
      </Button>
      <Button size="sm" variant="secondary" onClick={handleReset}>
        <MdOutlineRestartAlt size={15} /> Reset
      </Button>
      <Button size="sm" variant="ghost" onClick={() => setshowModal(false)}>
        Close
      </Button>
    </>
  );

  return (
    <Drawer
      show={showModal}
      onHide={() => setshowModal(false)}
      title={<><FaFilter className="tw-text-blue-500" size={13} /> Apply filters</>}
      backdropClick={false}
      footer={footer}
      width="50%"
      minWidth="480px"
    >
      <form id="logistic-filter-form" onSubmit={handleSubmit(onSubmit)}>
        <div className="tw-flex tw-items-start tw-gap-2 tw-bg-blue-50 tw-border tw-border-blue-100 tw-rounded tw-px-3 tw-py-2.5 tw-mb-4">
          <span className="tw-text-blue-500 tw-mt-0.5">ℹ</span>
          <p className="tw-text-xs tw-text-blue-700 tw-m-0">Narrow shipments by project, customs, forwarder and dates.</p>
        </div>

        <SectionTitle>Project information</SectionTitle>

        <FieldRow label="Job number">
          <input type="text" placeholder="Job number" className={inputClass} {...register("job_number")} />
        </FieldRow>

        <FieldRow label="Quote number">
          <input type="text" placeholder="Quote number" className={inputClass} {...register("quote_number")} />
        </FieldRow>

        <FieldRow label="Buyer">
          <input type="text" placeholder="Buyer" className={inputClass} {...register("buyer")} />
        </FieldRow>

        <FieldRow label="Delivery term">
          <Controller
            control={control}
            name="delivery_term"
            render={({ field }) => (
              <Select
                {...field}
                options={deliveryTermOptions}
                placeholder="Select..."
                classNamePrefix="react-select"
                value={deliveryTermOptions.filter(o => field.value?.includes(o.value))}
                onChange={opts => field.onChange(opts ? opts.map(o => o.value) : [])}
                isMulti
                closeMenuOnSelect={false}
              />
            )}
          />
        </FieldRow>

        <FieldRow label="Destination port">
          <input type="text" placeholder="Destination port" className={inputClass} {...register("destination_port")} />
        </FieldRow>

        <FieldRow label="Location">
          <Controller
            control={control}
            name="location"
            render={({ field }) => (
              <Select
                {...field}
                options={countryOptions}
                placeholder="Select..."
                classNamePrefix="react-select"
                value={countryOptions.filter(o => field.value?.includes(o.value))}
                onChange={opts => field.onChange(opts ? opts.map(o => o.value) : [])}
                isMulti
                closeMenuOnSelect={false}
              />
            )}
          />
        </FieldRow>

        <SectionTitle>Shipment information</SectionTitle>

        <FieldRow label="Phase name">
          <input type="text" placeholder="Phase name" className={inputClass} {...register("phase_name")} />
        </FieldRow>

        <FieldRow label="ETA">
          <Controller
            control={control}
            name="estimate_time_arrival"
            render={() => (
              <DatePicker
                onChange={onChangeETADate}
                startDate={etaFromDate}
                endDate={etaEndDate}
                selectsRange
                isClearable
                dateFormat="dd MMM yyyy"
                placeholderText="ETA date range"
                className={inputClass}
                showMonthDropdown
                useShortMonthInDropdown
              />
            )}
          />
        </FieldRow>

        <FieldRow label="Customs">
          <div className="tw-flex tw-gap-2">
            <input type="text" placeholder="Customs no." className={`${inputClass} tw-w-2/5`} {...register("customs_no")} />
            <Controller
              control={control}
              name="customs_date"
              render={() => (
                <DatePicker
                  onChange={onChangeCustomsDate}
                  startDate={customsFromDate}
                  endDate={customsEndDate}
                  selectsRange
                  isClearable
                  dateFormat="dd MMM yyyy"
                  placeholderText="Customs date range"
                  className={inputClass}
                  showMonthDropdown
                  useShortMonthInDropdown
                />
              )}
            />
          </div>
        </FieldRow>

        <FieldRow label="Bill no.">
          <input type="text" placeholder="Bill no." className={inputClass} {...register("bill_no")} />
        </FieldRow>

        <FieldRow label="Shipment no.">
          <input type="text" placeholder="Shipment no." className={inputClass} {...register("shipment_no")} />
        </FieldRow>

        <FieldRow label="Container type">
          <Controller
            control={control}
            name="container_type"
            render={({ field }) => (
              <Select
                {...field}
                options={containerTypeOptions}
                placeholder="Select..."
                classNamePrefix="react-select"
                value={containerTypeOptions.filter(o => field.value?.includes(o.value))}
                onChange={opts => field.onChange(opts ? opts.map(o => o.value) : [])}
                isMulti
                closeMenuOnSelect={false}
              />
            )}
          />
        </FieldRow>

        <FieldRow label="Forwarder">
          <Controller
            control={control}
            name="forwarder_id"
            render={({ field }) => (
              <AsyncSelect
                {...field}
                cacheOptions
                defaultOptions
                loadOptions={loadForwarderOptions}
                placeholder="Select..."
                classNamePrefix="react-select"
                isMulti
                closeMenuOnSelect={false}
                value={field.value || []}
                onChange={opts => field.onChange(opts)}
              />
            )}
          />
        </FieldRow>

        <SectionTitle>Submitted info</SectionTitle>

        <FieldRow label="Submitted by">
          <div className="tw-flex tw-gap-2">
            <input type="text" placeholder="Submitted by" className={`${inputClass} tw-w-2/5`} {...register("created_by")} />
            <Controller
              control={control}
              name="created_at"
              render={() => (
                <DatePicker
                  onChange={onChangeSubmitedDate}
                  startDate={submitedFromDate}
                  endDate={submitedEndDate}
                  selectsRange
                  isClearable
                  dateFormat="dd MMM yyyy"
                  placeholderText="Submitted date range"
                  className={inputClass}
                  showMonthDropdown
                  useShortMonthInDropdown
                />
              )}
            />
          </div>
        </FieldRow>
      </form>
    </Drawer>
  );
};

LogisticTrackingFilterAdvanceModal.propTypes = {
  showModal: PropTypes.bool.isRequired,
  setshowModal: PropTypes.func.isRequired,
  data: PropTypes.object,
  entryKey: PropTypes.string,
  onApply: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired,
};

export default LogisticTrackingFilterAdvanceModal;
